import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import Button from '@mui/material/Button';
import { organizationAPI } from '../../api/requests/organization/organizationAPI';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Pagination from '@mui/material/Pagination';
import Skeletons from '../Skeleton/Skeletons';
import { useNavigate } from 'react-router-dom';
import TaskIcon from '@mui/icons-material/Task';
import ImportContactsOutlinedIcon from '@mui/icons-material/ImportContactsOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import {
  Avatar,
  Drawer,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Chip,
  Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { handleAlert } from '../../utils/handleAlert';
import { courseAPI } from '../../api/requests/courses/courseAPI';
import CampaignIcon from '@mui/icons-material/Campaign';
import { getOrgName } from '../../utils/appendOrgQuery';
import { RoundedSelect } from '../Form/RoundedSelect';
import RoundedTextField from '../Form/RoundedTextField';
import { TaskAPI } from '../../api/requests/tasks/taskAPI';
import { NOTIFICATION_TYPE, ANNOUNCEMENT_TYPE, ANNOUNCEMENT_TYPE_TO_NOTIFICATION_TYPE } from '../../enums/notification';

const announcementTypeOptions = [
  { label: 'General', value: ANNOUNCEMENT_TYPE.GENERAL },
  { label: 'Course', value: ANNOUNCEMENT_TYPE.COURSE },
  { label: 'Task', value: ANNOUNCEMENT_TYPE.TASK },
];
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 1, pb: 2 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const Notifications_SideBar = ({ open, handleClose, setSelectedOrgName }) => {
  const theme = useTheme();
  const [value, setValue] = useState(0);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [activeTab, setActiveTab] = useState(0);

  const boxWidth = isDesktop ? 400 : 320;
  const handleChange = (event, newValue) => {
    setValue(newValue);
    setActiveTab(newValue);
  };

  const handleChangeIndex = (index) => {
    setValue(index);
  };

  const [unReadCount, setUnReadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notificationsList, setNotificationsList] = useState([]);
  const [openAnnouncementPopup, setOpenAnnouncementPopup] = useState(false);
  const [announcementType, setAnnouncementType] = useState('');
  const [announcementText, setAnnouncementText] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [courses, setCourses] = useState([]);
  const [coursePage, setCoursePage] = useState(1);
  const [courseTotalPages, setCourseTotalPages] = useState(1);
  const [courseSearch, setCourseSearch] = useState('');
  const [sectionSearch, setSectionSearch] = useState('');
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState('');
  const [tasks, setTasks] = useState([]);
  const [taskPage, setTaskPage] = useState(1);
  const [taskTotalPages, setTaskTotalPages] = useState(1);
  const [taskSearch, setTaskSearch] = useState('');
  const [selectedTask, setSelectedTask] = useState('');
  const [errors, setErrors] = useState({});
  const REQUIRED_FIELD_ERROR = 'This field is required';

  useEffect(() => {
    if (selectedCourse) {
      const fetchSections = async () => {
        try {
          const response = await courseAPI.getSpecificCourse(selectedCourse);
          if (response?.data?.sections) {
            setSections(response.data.sections);
          } else {
            setSections([]);
          }
        } catch (error) {
          console.error('Error fetching sections:', error);
          setSections([]);
        }
      };
      fetchSections();
    } else {
      setSections([]);
      setSelectedSection('');
    }
  }, [selectedCourse]);

  const fetchCourses = async (page = 1, searchValue = '') => {
    try {
      const res = await courseAPI.getUserCourses(page, null, searchValue);
      const coursesData = res?.data?.data || res?.data || [];
      setCourses(coursesData);
      setCoursePage(page);
      setCourseTotalPages(res?.pageCount ?? 1);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
      setCourseTotalPages(1);
    }
  };

  const fetchTasks = async (page = 1, searchValue = '') => {
    try {
      const res = await TaskAPI.getCreatedTasksWithSearch(page, searchValue);
      const tasksData = res?.data?.data || res?.data || [];
      setTasks(tasksData);
      setTaskPage(page);
      setTaskTotalPages(res?.pageCount ?? 1);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setTasks([]);
      setTaskTotalPages(1);
    }
  };

  const enrichNotificationMetadata = async (notifications = []) => {
    if (!notifications?.length) return [];

    const normalizedNotifications = notifications.map((notification) => {
      const type = notification.type;
      let courseTitle = notification.courseTitle || '';
      let sectionTitle = notification.sectionTitle || '';
      let taskTitle = notification.taskTitle || '';

      if (
        type === NOTIFICATION_TYPE.ANNOUNCEMENT_COURSE ||
        type === NOTIFICATION_TYPE.COURSE
      ) {
        courseTitle = courseTitle || notification?.metadata?.title || '';
        sectionTitle = sectionTitle || notification?.metadata?.sectionTitle || '';
      }
      if (
        type === NOTIFICATION_TYPE.ANNOUNCEMENT_TASK ||
        type === NOTIFICATION_TYPE.TASK
      ) {
        taskTitle = taskTitle || notification?.metadata?.title || '';
      }

      return {
        ...notification,
        message: notification.message || notification.announcement || '',
        logoLink:
          notification.logoLink ||
          notification.organization?.logoLink ||
          notification.data?.organization?.logoLink ||
          '',
        orgName:
          notification.orgName ||
          notification.organization?.name ||
          notification.name ||
          '',
        courseTitle,
        sectionTitle,
        taskTitle,
      };
    });

    const courseIds = new Set();
    const taskIds = new Set();

    normalizedNotifications.forEach((notification) => {
      const type = notification.type;
      if (
        type === NOTIFICATION_TYPE.ANNOUNCEMENT_COURSE ||
        type === NOTIFICATION_TYPE.COURSE
      ) {
        if (notification.serviceId && !notification.courseTitle) {
          courseIds.add(notification.serviceId);
        } else if (notification.courseId && !notification.courseTitle) {
          courseIds.add(notification.courseId);
        }
        if (notification.sectionId && !notification.sectionTitle) {
          courseIds.add(notification.courseId || notification.serviceId);
        }
      }

      if (
        type === NOTIFICATION_TYPE.ANNOUNCEMENT_TASK ||
        type === NOTIFICATION_TYPE.TASK
      ) {
        if (notification.serviceId && !notification.taskTitle) {
          taskIds.add(notification.serviceId);
        }
      }
    });

    const courseMap = {};
    const sectionMap = {};
    const taskMap = {};

    await Promise.all([
      ...Array.from(courseIds).map(async (courseId) => {
        try {
          const response = await courseAPI.getSpecificCourse(courseId);
          const course = response?.data?.courseData || response?.data || response?.courseData;
          if (course) {
            courseMap[courseId] = course.title || course.name || '';
            if (Array.isArray(course.sections)) {
              course.sections.forEach((section) => {
                if (section?.id) {
                  sectionMap[section.id] = section.sectionTitle || section.name || '';
                }
              });
            }
          }
        } catch (err) {
          courseMap[courseId] = courseMap[courseId] || '';
        }
      }),
      ...Array.from(taskIds).map(async (taskId) => {
        try {
          const response = await TaskAPI.getTaskData(taskId);
          const task = response?.data || response;
          taskMap[taskId] = task?.title || task?.name || '';
        } catch (err) {
          taskMap[taskId] = taskMap[taskId] || '';
        }
      }),
    ]);

    return normalizedNotifications.map((notification) => {
      const enriched = { ...notification };
      const type = notification.type;

      if (
        type === NOTIFICATION_TYPE.ANNOUNCEMENT_COURSE ||
        type === NOTIFICATION_TYPE.COURSE
      ) {
        enriched.courseTitle =
          enriched.courseTitle || courseMap[notification.serviceId] || courseMap[notification.courseId] || '';
        if (notification.sectionId) {
          enriched.sectionTitle =
            enriched.sectionTitle || sectionMap[notification.sectionId] || '';
        }
      }

      if (type === NOTIFICATION_TYPE.ANNOUNCEMENT_TASK) {
        enriched.taskTitle = enriched.taskTitle || taskMap[notification.serviceId] || '';
      }

      return enriched;
    });
  };

  const handleOpenAnnounce = async () => {
    setOpenAnnouncementPopup(true);
    await Promise.all([
      fetchCourses(1, courseSearch),
      fetchTasks(1, taskSearch)
    ]);
  };

  const handleCourseSearch = async (event) => {
    const searchValue = event.target.value;
    setCourseSearch(searchValue);
    await fetchCourses(1, searchValue);
  };

  const handleTaskSearch = async (event) => {
    const searchValue = event.target.value;
    setTaskSearch(searchValue);
    await fetchTasks(1, searchValue);
  };

  const handleSectionSearch = (event, newValue, reason) => {
    if (reason === 'input') {
      setSectionSearch(newValue);
    }
  };

  const handleCoursePageChange = async (event, value) => {
    setCoursePage(value);
    await fetchCourses(value, courseSearch);
  };

  const handleTaskPageChange = async (event, value) => {
    setTaskPage(value);
    await fetchTasks(value, taskSearch);
  };

  const handleCloseAnnounce = () => {
    setOpenAnnouncementPopup(false);
    setAnnouncementType('');
    setAnnouncementText('');
    setSelectedCourse('');
    setSelectedSection('');
    setSections([]);
    setSelectedTask('');
    setTasks([]);
    setErrors({});
    setCourseSearch('');
    setSectionSearch('');
    setTaskSearch('');
    setCoursePage(1);
    setCourseTotalPages(1);
    setTaskPage(1);
    setTaskTotalPages(1);
  };

  const handleAnnounceSubmit = async () => {
    let newErrors = {};
    if (!announcementType) newErrors.announcementType = REQUIRED_FIELD_ERROR;
    if (!announcementText) newErrors.announcementText = REQUIRED_FIELD_ERROR;
    if (announcementType === 'Course' && !selectedCourse)
      newErrors.selectedCourse = REQUIRED_FIELD_ERROR;
    if (announcementType === 'Task' && !selectedTask)
      newErrors.selectedTask = REQUIRED_FIELD_ERROR;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    let sectionName = null;
    let courseName = null;
    let taskName = null;

    if (announcementType === 'Course') {
      if (selectedSection) {
        const sectionObj = sections.find((s) => String(s.id) === String(selectedSection));
        if (sectionObj) {
          sectionName = sectionObj.sectionTitle;
        }
      }
      if (selectedCourse) {
        const courseObj = courses.find((c) => String(c.id) === String(selectedCourse));
        if (courseObj) {
          courseName = courseObj.title;
        }
      }
    } else if (announcementType === 'Task') {
      if (selectedTask) {
        const taskObj = tasks.find((t) => String(t.id) === String(selectedTask));
        if (taskObj) {
          taskName = taskObj.title;
        }
      }
    }

    const typeValue = ANNOUNCEMENT_TYPE_TO_NOTIFICATION_TYPE[announcementType];
    if (!typeValue) {
      handleAlert('Invalid announcement type selected', 'error');
      return;
    }

    const announcementPayload = {
      message: announcementText,
      type: typeValue,
    };

    if (announcementType === ANNOUNCEMENT_TYPE.COURSE) {
      announcementPayload.serviceId = selectedCourse;
      if (selectedSection) {
        announcementPayload.sectionId = selectedSection;
      }
    } else if (announcementType === ANNOUNCEMENT_TYPE.TASK) {
      announcementPayload.serviceId = selectedTask;
    }

    try {
      await organizationAPI.postAnnouncement(announcementPayload);
      await fetchNotifications();
      handleCloseAnnounce();
    } catch (error) {
      console.error('Error creating announcement:', error);
      handleAlert('Unable to create announcement. Please try again.', 'error');
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoadingNotifications(true);
      const response = await organizationAPI.getNotifications();
      const notifications = Array.isArray(response?.notifications)
        ? response.notifications
        : Array.isArray(response)
          ? response
          : [];

      const enrichedNotifications = await enrichNotificationMetadata(notifications);
      enrichedNotifications.sort((a, b) => {
        const aTime = new Date(a.created_at || a.createdAt).getTime();
        const bTime = new Date(b.created_at || b.createdAt).getTime();
        return bTime - aTime;
      });
      setNotificationsList(enrichedNotifications);
      setUnReadCount(response?.unreadCount ?? 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotificationsList([]);
      setUnReadCount(0);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const markAllAsRead = async () => {
    try {
      await organizationAPI.markNotificationsAsRead();
      setUnReadCount(0);
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const [loadingInvites, setLoadingInvites] = useState(false);
  const [invitesRecieved, setInvitesReceived] = useState([]);
  const fetchInvitesReceivedByOrganization = async () => {
    try {
      setLoadingInvites(true);
      const responseInvitesReceived = await organizationAPI.getInvitesRecievedByUser();
      setInvitesReceived(responseInvitesReceived);
    } catch (error) {
      console.error('Error fetching suggested organizations:', error);
    } finally {
      setLoadingInvites(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    const loadData = async () => {
      try {
        if (activeTab === 0) {
          await fetchNotifications();
          await markAllAsRead();
        } else if (activeTab === 1) {
          await fetchInvitesReceivedByOrganization();
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    loadData();
  }, [open, activeTab]);

  const navigate = useNavigate();

  const handleNotificationClick = (notification) => {
    const orgName = notification.orgName || getOrgName();
    switch (notification.type) {
      case NOTIFICATION_TYPE.TASK:
      case NOTIFICATION_TYPE.ANNOUNCEMENT_TASK:
        navigate(`/org/${notification.name}/task/${notification.serviceId}`);
        break;
      case 'CONTEST':
        navigate(`/org/${notification.name}/contest/${notification.serviceId}`);
        break;
      case NOTIFICATION_TYPE.COURSE:
        navigate(`/org/${orgName}/course/videos/${notification.courseId}`);
        break;
      case NOTIFICATION_TYPE.ANNOUNCEMENT_COURSE: {
        const sectionId = notification.sectionId ?? null;
        navigate(
          `/org/${orgName}/course/videos/${notification.courseId || notification.serviceId}?fromAnnouncementTab=true`,
          sectionId ? { state: { sectionId } } : undefined,
        );
        break;
      }
      default:
        break;
    }
    handleClose();
  };

  const handleClearAll = async () => {
    try {
      await organizationAPI.deleteNotifications();
      setNotificationsList([]);
      setUnReadCount(0);
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const openView = (organizationName) => {
    setSelectedOrgName(organizationName);
  };

  const rejectInvite = (id) => {
    organizationAPI
      .inviteAction(id, 'REJECT')
      .then(() => {
        handleAlert('Invite rejected successfully', 'success');
        fetchInvitesReceivedByOrganization();
      })
      .catch(() => {
        handleAlert('Error rejecting invite', 'error');
      });
  };

  const acceptInvite = (id) => {
    organizationAPI
      .inviteAction(id, 'ACCEPT')
      .then(() => {
        handleAlert('Invite accepted successfully', 'success');
        fetchInvitesReceivedByOrganization();
      })
      .catch(() => {
        handleAlert('Error accepting invite', 'error');
      });
  };

  const inputSx = {
    '& .MuiInputBase-input': { color: 'var(--color-text)' },
    '& .MuiInputBase-input.Mui-disabled': {
      color: 'var(--color-text-secondary) !important',
      WebkitTextFillColor: 'var(--color-text-secondary) !important',
      opacity: 1,
    },
    '& .MuiAutocomplete-input.Mui-disabled': {
      color: 'var(--color-text-secondary) !important',
      WebkitTextFillColor: 'var(--color-text-secondary) !important',
      opacity: 1,
    },
    '& .MuiInputLabel-root': { color: 'var(--color-text-secondary)' },
    '& .MuiInputLabel-root.Mui-disabled': {
      color: 'var(--color-text-secondary)',
      opacity: 0.6,
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'var(--color-bg-default)',
      borderRadius: '12px',
    },
    '& .MuiOutlinedInput-root.Mui-disabled': {
      backgroundColor: 'var(--color-bg-secondary)',
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--color-border)',
    },
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box sx={{ width: boxWidth }}>
        <Box
          sx={{
            py: 2,
            pl: 2,
            pr: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <Typography variant="h6">Notifications</Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              variant="outlined"
              size="small"
              sx={{ textTransform: 'capitalize', minWidth: 'fit-content', px: 1.5 }}
              onClick={handleOpenAnnounce}
            >
              Announce
            </Button>

            {notificationsList.length > 0 && value === 0 && (
              <Button
                variant="contained"
                size="small"
                sx={{ minWidth: 'fit-content', px: 1.5 }}
                onClick={handleClearAll}
              >
                Clear
              </Button>
            )}

            <IconButton onClick={handleClose} sx={{ p: 0.5, ml: { xs: 'auto', sm: 0 } }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <AppBar position="static" sx={{ background: 'transparent', boxShadow: 'none' }}>
          <Tabs value={value} onChange={handleChange} variant="fullWidth">
            <Tab label="Notification" sx={{ textTransform: 'capitalize' }} />
            <Tab label="Invites received" sx={{ textTransform: 'capitalize' }} />
          </Tabs>
        </AppBar>

        <Box sx={{ width: '100%', height: 'calc(100vh - 150px)' }}>
          <TabPanel value={value} index={0} dir={theme.direction}>
            {loadingNotifications ? (
              <Skeletons type="smallCircularLoader" />
            ) : (
              <Box sx={{ overflowY: 'auto' }}>
                {notificationsList.map((notification) => {
                  let iconComponent;
                  switch (notification.type) {
                    case NOTIFICATION_TYPE.TASK:
                    case NOTIFICATION_TYPE.ANNOUNCEMENT_TASK:
                      iconComponent = <TaskIcon fontSize="small" />;
                      break;
                    case 'CONTEST':
                      iconComponent = <EmojiEventsIcon fontSize="small" />;
                      break;
                    case NOTIFICATION_TYPE.COURSE:
                    case NOTIFICATION_TYPE.ANNOUNCEMENT_COURSE:
                      iconComponent = <ImportContactsOutlinedIcon fontSize="small" />;
                      break;
                    case NOTIFICATION_TYPE.ANNOUNCEMENT_GENERAL:
                      iconComponent = <CampaignIcon fontSize="small" />;
                      break;
                    default:
                      iconComponent = <NotificationsIcon fontSize="small" />;
                      break;
                  }

                  return (
                    <Box
                      key={notification.id}
                      sx={{
                        padding: '12px 16px',
                        width: '100%',
                        color: 'var(--color-text)',
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid var(--color-border)',
                        fontWeight: notification.status === 'NEW' ? '600' : '400',
                        opacity: notification.status === 'NEW' ? 1 : 0.7,
                        backgroundColor: 'transparent',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: 'var(--color-bg-secondary)',
                        },
                      }}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <Box sx={{ mt: 0.5 }}>{iconComponent}</Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 1, width: '100%' }}>
                          <Typography variant="body2" sx={{ color: 'var(--color-text)' }}>
                            {notification.message}
                          </Typography>
                          {(notification.logoLink || notification.orgName) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {notification.orgName ? (
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'var(--color-text-secondary)' }}
                                >
                                  {notification.orgName}
                                </Typography>
                              ) : null}
                            </Box>
                          )}
                        </Box>
                        {(notification.type === NOTIFICATION_TYPE.ANNOUNCEMENT_COURSE ||
                          notification.type === NOTIFICATION_TYPE.ANNOUNCEMENT_TASK) &&
                          (notification.courseTitle || notification.sectionTitle || notification.taskTitle) && (
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {notification.courseTitle && (
                              <Chip
                                size="small"
                                label={notification.sectionTitle ? `${notification.courseTitle} >` : `Course: ${notification.courseTitle}`}
                                sx={{
                                  color: 'var(--color-primary)',
                                  borderColor: 'var(--color-primary)',
                                  height: 'auto',
                                  '& .MuiChip-label': {
                                    display: 'block',
                                    whiteSpace: 'normal',
                                    paddingTop: '3px',
                                    paddingBottom: '3px'
                                  },
                                  fontSize: '0.75rem',
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                    borderColor: 'var(--color-primary)'
                                    },
                                }}
                                variant="outlined"
                              />
                            )}
                            {notification.sectionTitle && (
                              <Chip
                                size="small"
                                label={`Section: ${notification.sectionTitle}`}
                                sx={{
                                  color: 'var(--color-text-secondary)',
                                  borderColor: 'var(--color-border)',
                                  height: 'auto',
                                  '& .MuiChip-label': {
                                    display: 'block',
                                    whiteSpace: 'normal',
                                    paddingTop: '3px',
                                    paddingBottom: '3px'
                                  },
                                  fontSize: '0.75rem',
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                    borderColor: 'var(--color-border)'
                                    },
                                }}
                                variant="outlined"
                              />
                            )}
                            {notification.taskTitle && (
                              <Chip
                                size="small"
                                label={`Task: ${notification.taskTitle}`}
                                sx={{
                                  color: 'var(--color-secondary)',
                                  borderColor: 'var(--color-border)',
                                  height: 'auto',
                                  '& .MuiChip-label': {
                                    display: 'block',
                                    whiteSpace: 'normal',
                                    paddingTop: '3px',
                                    paddingBottom: '3px'
                                  },
                                  fontSize: '0.75rem',
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                    borderColor: 'var(--color-border)'
                                  },
                                }}
                                variant="outlined"
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}
                {notificationsList.length === 0 && (
                  <Typography sx={{ textAlign: 'center', p: 2 }}>No New Notifications</Typography>
                )}
              </Box>
            )}
          </TabPanel>

          <TabPanel value={value} index={1} dir={theme.direction}>
            {loadingInvites ? (
              <Skeletons type="smallCircularLoader" />
            ) : (
              <>
                {invitesRecieved?.map((member) => (
                  <Box
                    key={member?.id}
                    display={'flex'}
                    width={'100%'}
                    justifyContent={'space-between'}
                    flexDirection={'column'}
                    alignItems={'center'}
                    sx={{ py: 1.5, borderBottom: '1px solid var(--color-border)', rowGap: 1 }}
                  >
                    <Box
                      display={'flex'}
                      gap={1}
                      alignItems={'flex-start'}
                      justifyContent={'space-between'}
                      width={'-webkit-fill-available'}
                    >
                      <Box display={'flex'} gap={1} alignItems={'flex-start'}>
                        <Avatar
                          src={member?.organization?.logoLink}
                          alt={member?.organization?.name}
                          style={{
                            backgroundColor: member?.organization?.logoLink
                              ? 'transparent'
                              : 'var(--color-primary)',
                            color: member?.organization?.logoLink ? 'var(--color-text)' : 'white',
                            width: '30px',
                            height: '30px',
                            fontSize: '19px',
                          }}
                        >
                          {member?.organization?.name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <Typography variant="body1" sx={{ fontWeight: '500' }}>
                          {`${member?.organization?.name}`}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 'fit-content', textTransform: 'capitalize' }}
                        onClick={() => openView(member?.organization?.name)}
                      >
                        View Org
                      </Button>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        width: '-webkit-fill-available',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        size="small"
                        variant="text"
                        sx={{ minWidth: 'fit-content', textTransform: 'capitalize' }}
                        onClick={() => rejectInvite(member?.id)}
                      >
                        Reject
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        sx={{ minWidth: 'fit-content', textTransform: 'capitalize' }}
                        onClick={() => acceptInvite(member?.id)}
                      >
                        Accept
                      </Button>
                    </Box>
                  </Box>
                ))}
                {invitesRecieved?.length === 0 && (
                  <Typography sx={{ textAlign: 'center', p: 2 }}>No Invites Received</Typography>
                )}
              </>
            )}
          </TabPanel>
        </Box>
      </Box>

      <Dialog open={openAnnouncementPopup} onClose={handleCloseAnnounce} maxWidth="sm" fullWidth>
        <DialogTitle>Make an Announcement</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Autocomplete
              options={announcementTypeOptions}
              value={
                announcementTypeOptions.find(
                  (opt) => opt.value === announcementType
                ) || null
              }
              inputValue={announcementType}
              onInputChange={(event, newValue, reason) => {
                if (reason === 'input') {
                  setAnnouncementType(newValue);
                }
                if (reason === 'clear') {
                  setAnnouncementType('');
                }
              }}
              onChange={(_, newValue) => {
                if (newValue) {
                  setAnnouncementType(newValue.value);
                  setErrors((prev) => ({ ...prev, announcementType: '' }));
                } else {
                  setAnnouncementType('');
                }
              }}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              openOnFocus
              renderInput={(params) => (
                <RoundedTextField
                  {...params}
                  label="Type *"
                  placeholder="Select type..."
                  error={!!errors.announcementType}
                  helperText={errors.announcementType}
                  sx={{
                    ...inputSx,
                  }}
                />
              )}
            />
            {errors.announcementType && (
              <Typography variant="caption" sx={{ color: 'red', ml: 1 }}>
                {errors.announcementType}
              </Typography>
            )}
          </Box>
          <TextField
            autoFocus
            label="Text"
            required
            multiline
            rows={4}
            fullWidth
            value={announcementText}
            onChange={(e) => {
              setAnnouncementText(e.target.value);
              setErrors((prev) => ({ ...prev, announcementText: '' }));
            }}
            sx={{
              ...inputSx,
              ...(errors.announcementText && {
                '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'red !important',
                },
              }),
            }}
            error={!!errors.announcementText}
            helperText={errors.announcementText}
            FormHelperTextProps={{
              sx: { color: 'red', ml: 1 },
            }}
          />
          {announcementType === ANNOUNCEMENT_TYPE.COURSE && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Autocomplete
                  options={courses}
                  value={courses.find((c) => String(c.id) === String(selectedCourse)) || null}
                  inputValue={courseSearch}
                  onInputChange={(e, val, reason) => {
                    if (reason === 'input') handleCourseSearch(e);
                    if (reason === 'clear') setCourseSearch('');
                  }}
                  onChange={(_, val) => {
                    if (val) {
                      setSelectedCourse(val.id);
                      setCourseSearch(val.title);
                      setSelectedSection('');
                    } else {
                      setSelectedCourse('');
                      setCourseSearch('');
                    }
                  }}
                  getOptionLabel={(option) => option.title || ''}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  renderInput={(params) => (
                    <RoundedTextField
                      {...params}
                      label="Select Course *"
                      placeholder="Select course..."
                      error={!!errors.selectedCourse}
                      helperText={errors.selectedCourse}
                      sx={inputSx}
                    />
                  )}
                />
              </Box>
              {courseTotalPages > 1 && (
                <Pagination
                  count={courseTotalPages}
                  page={coursePage}
                  onChange={handleCoursePageChange}
                  size="small"
                  sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}
                />
              )}
              <Autocomplete
                options={sections}
                value={sections.find((s) => String(s.id) === String(selectedSection)) || null}
                inputValue={sectionSearch}
                onInputChange={(e, val, reason) => {
                  if (reason === 'input') setSectionSearch(val);
                  if (reason === 'clear') setSectionSearch('');
                }}
                onChange={(_, val) => {
                  if (val) {
                    setSelectedSection(val.id);
                    setSectionSearch(val.sectionTitle);
                  } else {
                    setSelectedSection('');
                    setSectionSearch('');
                  }
                }}
                getOptionLabel={(option) => option.sectionTitle || ''}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                disabled={!selectedCourse}
                noOptionsText={selectedCourse ? 'No sections available' : 'Select course first'}
                renderInput={(params) => (
                  <RoundedTextField
                    {...params}
                    label="Select Section"
                    placeholder="Select section..."
                    sx={inputSx}
                  />
                )}
              />
            </>
          )}
          {announcementType === ANNOUNCEMENT_TYPE.TASK && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Autocomplete
                  options={tasks || []}
                  value={tasks.find((t) => String(t.id) === String(selectedTask)) || null}
                  inputValue={taskSearch}
                  onInputChange={(event, newValue, reason) => {
                    if (reason === 'input') {
                      setTaskSearch(newValue);
                      handleTaskSearch({ target: { value: newValue } });
                    }
                    if (reason === 'clear') {
                      setTaskSearch('');
                      setSelectedTask('');
                    }
                  }}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      setSelectedTask(newValue.id);
                      setTaskSearch(newValue.title);
                      setErrors((prev) => ({ ...prev, selectedTask: '' }));
                    } else {
                      setSelectedTask('');
                      setTaskSearch('');
                    }
                  }}
                  getOptionLabel={(option) => option.title || ''}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  openOnFocus
                  disabled={!tasks || tasks.length === 0}
                  noOptionsText="No tasks available"
                  PaperComponent={({ children }) => (
                    <Box
                      sx={{
                        mt: 1,
                        borderRadius: '16px',
                        overflow: 'hidden',
                        boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
                        border: '1px solid var(--color-border)',
                        background: 'var(--color-bg-default)',
                      }}
                    >
                      {children}
                    </Box>
                  )}
                  renderOption={(props, task) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2,
                        py: 1.2,
                        transition: 'all 0.2s ease',
                        borderBottom: '1px solid var(--color-border)',
                        '&:last-child': { borderBottom: 'none' },
                        '&:hover': {
                          backgroundColor: 'var(--color-bg-secondary)',
                          transform: 'scale(1.01)',
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 34,
                          height: 34,
                          fontSize: 14,
                          bgcolor: 'var(--color-secondary)',
                        }}
                      >
                        {task.title?.charAt(0)?.toUpperCase()}
                      </Avatar>

                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                          {task.title}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                          {task.description || `Task ID ${task.id}`}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <RoundedTextField
                      {...params}
                      label="Select Task *"
                      placeholder="Search tasks..."
                      error={!!errors.selectedTask}
                      helperText={errors.selectedTask}
                      sx={{
                        ...inputSx,
                        ...(errors.selectedTask && {
                          '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'red !important',
                          },
                        }),
                      }}
                    />
                  )}
                />
              </Box>
              {taskTotalPages > 1 && (
                <Pagination
                  count={taskTotalPages}
                  page={taskPage}
                  onChange={handleTaskPageChange}
                  size="small"
                  sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAnnounce} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleAnnounceSubmit} variant="contained" color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default Notifications_SideBar;
